import { useQuery } from '@apollo/client';
import {
  type AssetClassColor,
  type AssetClassColorSource,
  buildAssetClassColorMap,
  buildInViewAssetClassColorMap,
  resolveAssetClassColorFromMap,
} from '@ovComponents/2-component/pieChart/getChartColors';
import { UserContext } from '@providers/userContextProvider';
import { useCallback, useContext, useMemo } from 'react';
import { FETCH_ASSET_CLASS_COLOR_REGISTRY } from './useAssetClassColors.query';

/** Past this the tail of the list falls to the default. The largest org here holds eighty. */
const REGISTRY_PAGE_SIZE = 250;

export type AssetClassColorResolver = (
  assetClass: AssetClassColorSource | null | undefined,
) => AssetClassColor;

export type AssetClassColors = {
  resolve: AssetClassColorResolver;
  /**
   * True while the registry is still in flight, and while the organization it
   * is keyed to has yet to resolve. `resolve` answers throughout, but from an
   * empty registry, so every asset class takes the default and loses it once
   * the registry lands. Hold off painting until this clears rather than show a
   * colour that then changes.
   */
  loading: boolean;
};

/**
 * Resolves an asset class to a colour it keeps in every chart and table, so it
 * no longer changes with the portfolio being viewed.
 *
 * The organization's asset class list decides, and position in it picks the
 * colour. Anything the list does not hold takes the default, which is rare
 * enough to leave at that — an asset class archived since a security first
 * referenced it, or one past the page we ask for.
 *
 * Pass the asset classes currently on screen to guarantee they also differ from
 * one another, which is what keeps two of them off the same colour. Every
 * surface in one view has to pass the same set, which it gets by deriving from
 * the securities they share. Hold that array steady between renders, or the
 * colours are recomputed on each one.
 *
 * Cached by Apollo and shared with the other `fetchAssetClasses` callers, so
 * rendering several charts costs one request per session. That first request is
 * the one window where colours are not yet settled, which `loading` marks: see
 * the note on it before painting anything from `resolve`.
 */
export const useAssetClassColors = (
  assetClassesInView?: readonly (AssetClassColorSource | null | undefined)[],
): AssetClassColors => {
  const { activeOrganization } = useContext(UserContext);
  const organizationId = activeOrganization?.id;

  const { data, loading } = useQuery(FETCH_ASSET_CLASS_COLOR_REGISTRY, {
    skip: !organizationId,
    fetchPolicy: 'cache-first',
    variables: {
      input: {
        filter: { organizationId },
        pagination: { perPage: REGISTRY_PAGE_SIZE },
      },
    },
  });

  const registry = useMemo(() => {
    const assetClasses: AssetClassColorSource[] =
      data?.fetchAssetClasses?.assetClasses ?? [];

    // Sorted by id so the order does not depend on how the server returned it.
    // An asset class id is an ObjectID, which leads with a creation timestamp
    // and is fixed width, so sorting them as strings sorts them by age: a new
    // asset class lands at the end and takes the next free slot rather than
    // recolouring the ones already in use. Deleting one still shifts every
    // colour after it, which is the case this ordering cannot cover.
    return [...assetClasses].sort((a, b) =>
      String(a.id ?? '').localeCompare(String(b.id ?? '')),
    );
  }, [data]);

  const colorByAlias = useMemo(
    () => buildAssetClassColorMap(registry),
    [registry],
  );

  const colorByAliasInView = useMemo(
    () =>
      assetClassesInView?.length
        ? buildInViewAssetClassColorMap(registry, assetClassesInView)
        : colorByAlias,
    [registry, colorByAlias, assetClassesInView],
  );

  const resolve = useCallback<AssetClassColorResolver>(
    (assetClass) =>
      resolveAssetClassColorFromMap(colorByAliasInView, assetClass),
    [colorByAliasInView],
  );

  // Skipping the query leaves `loading` false, which would report an empty
  // registry as the finished one and let callers paint colours they then lose.
  return useMemo(
    () => ({ resolve, loading: loading || !organizationId }),
    [resolve, loading, organizationId],
  );
};
